/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 전체 컴포넌트를 관리하는 클래스를 정의한다.
 *
 * @file /src/base/Components.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 25.
 */
import { Component } from '@base/Component';
import { Html } from '@utils/Html';
import { Exceptions } from '@utils/Exceptions';

export class Components {
    /**
     * 모든 컴포넌트 객체
     */
    static components: Map<string, Component> = new Map();

    /**
     * 컴포넌트 인덱스
     */
    static componentIndex: number = 0;

    /**
     * 컴포넌트 고유값을 생성한다.
     *
     * @returns 컴포넌트 고유값
     */
    static createId(): string {
        return 'Aui-' + ++this.componentIndex;
    }

    /**
     * 컴포넌트를 등록한다.
     *
     * @param component - 등록할 컴포넌트
     */
    static set(component: Component): void {
        if (this.components.has(component.id) == true) {
            Exceptions.warn('DUPLICATED_COMPONENT_ID', component.id);
        }

        this.components.set(component.id, component);
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param component - 제거할 컴포넌트
     */
    static remove(component: Component): void {
        this.components.delete(component.id);
    }

    /**
     * 컴포넌트를 가져온다.
     *
     * @param id - 컴포넌트 고유값
     * @returns 컴포넌트
     */
    static get(id: string): Component | undefined {
        return this.components.get(id);
    }

    /**
     * 드랍존이 있는 컴포넌트 캐시
     */
    static droppableComponents: Set<Component> = new Set();

    /**
     * 파일 드래그가 브라우저 안으로 최초 진입하였을 때, droppable 컴포넌트를 수집하고 활성화한다.
     */
    static fileDragEnter(): void {
        if (this.droppableComponents.size === 0) {
            for (const component of this.components.values()) {
                if (component.$droppable !== null) {
                    this.droppableComponents.add(component);
                }
            }
        }
        this.fileDragEnabled();
    }

    /**
     * 파일 드래그가 브라우저 밖으로 이탈하거나 drop 되었을 때, droppable 컴포넌트를 비활성화하고 캐시를 초기화한다.
     */
    static fileDragLeave(): void {
        this.fileDragDisabled();
        this.droppableComponents.clear();
    }

    /**
     * 캐시된 droppable 컴포넌트 전체에 droppable 클래스를 부여한다.
     */
    static fileDragEnabled(): void {
        for (const component of this.droppableComponents) {
            component.onFileDrag(true, null);
        }
    }

    /**
     * 캐시된 droppable 컴포넌트 전체에서 droppable, dragenter 클래스를 제거한다.
     *
     * @param entered - 파일 드래그를 받은 컴포넌트
     */
    static fileDragDisabled(entered: Component = null): void {
        for (const component of this.droppableComponents) {
            if (entered?.id === component.id) {
                component.onFileDrag(true, true);
            } else {
                component.onFileDrag(false, false);
            }
        }
    }

    /**
     * 컴포넌트의 keydown 이벤트를 처리한다.
     *
     * @param e - 키보드이벤트
     */
    static keydownQueue: Promise<void> = Promise.resolve();
    static keydown(e: KeyboardEvent): void {
        Components.keydownQueue = Components.keydownQueue
            .then(async () => {
                if (!(document.activeElement instanceof HTMLElement)) {
                    return;
                }

                let $component =
                    Html.el(document.activeElement).getParents('aui-component') ??
                    Html.el(document.activeElement).getParents('dialog[data-component]');

                while ($component !== null) {
                    const component = Components.get($component.getAttr('data-component'));
                    if (component != null) {
                        if (component.isKeyboard() == true) {
                            if ((await component.onKeyboard(e)) === false || (e as any).cancelBubble === true) {
                                break;
                            }
                        }
                    }
                    $component =
                        $component.getParent()?.getParents('aui-component') ??
                        $component.getParent()?.getParents('dialog[data-component]');
                }
            })
            .catch(() => {});
    }

    /**
     * popover 바깥부분 클릭 이벤트를 처리한다.
     *
     * @param e - 마우스이벤트
     */
    static pointerdown(e: MouseEvent): void {
        const $target = Html.el(e.target);
        const exclude = $target?.getParents('*[data-popover]')?.getAttr('data-popover') ?? null;

        Html.all('[popover]:popover-open').forEach(($dom) => {
            if ($dom.getAttr('data-role') == 'resize-guide') {
                return;
            }

            if ($dom.getAttr('data-popover') == exclude) {
                return;
            }

            if ($dom.hasChild($target) == true) {
                return;
            }

            $dom.getEl().hidePopover();
        });
    }

    /**
     * focusin 이벤트 처리 큐
     */
    static focusQueue: Promise<void> = Promise.resolve();

    /**
     * onBlur 또는 onFocus 버블링을 처리한다.
     *
     * @param $eventTarget - 버블링 기준 요소 (blur: 포커스를 잃는 요소 / focus: 포커스를 받는 요소)
     * @param $counterTarget - 상대 요소 (blur: 포커스를 받는 요소 / focus: 포커스를 잃는 요소), null 이면 페이지 이탈 또는 외부 진입
     * @param type - 이벤트 타입 ('blur' 또는 'focus')
     */
    private static async bubbleFocusEvent(
        $eventTarget: ReturnType<typeof Html.el>,
        $counterTarget: ReturnType<typeof Html.el>,
        type: 'blur' | 'focus'
    ): Promise<void> {
        let $node = $eventTarget.getParents('aui-component') ?? $eventTarget.getParents('dialog[data-component]');

        /**
         * 상대 요소가 연계된 컴포넌트 내부에 있을 경우, 연계된 컴포넌트 ID 를 구한다.
         */
        let related: string = null;
        if ($counterTarget !== null) {
            const $related =
                $counterTarget.getParents('aui-component[data-target-component]') ??
                $counterTarget.getParents('dialog[data-target-component]');

            related = $related?.getAttr('data-target-component') ?? null;
        }

        while ($node !== null) {
            /**
             * 현재 컴포넌트가 상대 요소의 연계 컴포넌트인 경우,
             * 현재 컴포넌트는 이미 포커스 상태가 유지되므로 이벤트를 호출하지 않는다.
             */
            if (related !== null && $node.getAttr('data-component') == related) {
                return;
            }

            /**
             * 상대 요소가 현재 컴포넌트 내부에 있다면,
             * 현재 컴포넌트는 포커스 상태가 유지되므로 이벤트를 호출하지 않는다.
             */
            if ($counterTarget !== null && $node.hasChild($counterTarget) == true) {
                break;
            }

            const component = Components.get($node.getAttr('data-component'));
            if (component != null) {
                if ((await (type === 'blur' ? component.onBlur() : component.onFocus())) === false) {
                    break;
                }
            }

            /**
             * 현재 컴포넌트가 특정 컴포넌트와 연결되어 있는 경우, 해당 컴포넌트에도 이벤트를 호출한다.
             */
            if ($node.getAttr('data-target-component') !== null) {
                let $related = Components.get($node.getAttr('data-target-component'))?.$getComponent() ?? null;
                while ($related !== null) {
                    /**
                     * 상대 요소가 연결된 컴포넌트 내부에 있다면, 이벤트를 호출하지 않는다.
                     */
                    if ($counterTarget !== null && $related.hasChild($counterTarget) == true) {
                        break;
                    }

                    const relatedComponent = Components.get($related.getAttr('data-component'));
                    if (relatedComponent !== null) {
                        if (
                            (await (type === 'blur' ? relatedComponent.onBlur() : relatedComponent.onFocus())) === false
                        ) {
                            break;
                        }
                    }

                    $related =
                        $related.getParent()?.getParents('aui-component') ??
                        $related.getParent()?.getParents('dialog[data-component]');
                }
            }

            $node =
                $node.getParent()?.getParents('aui-component') ??
                $node.getParent()?.getParents('dialog[data-component]');
        }
    }

    /**
     * 컴포넌트 포커스 이벤트를 처리한다.
     * 포커스를 잃는 쪽의 onBlur 와 포커스를 받는 쪽의 onFocus 를 모두 처리한다.
     *
     * @param e - 포커스이벤트
     */
    static focusin(e: FocusEvent): void {
        Components.focusQueue = Components.focusQueue
            .then(async () => {
                if (!(e.target instanceof HTMLElement)) {
                    return;
                }

                const $target = Html.el(e.target);
                const $relatedTarget = Html.el(e.relatedTarget as HTMLElement | null);

                if ($relatedTarget !== null) {
                    await Components.bubbleFocusEvent($relatedTarget, $target, 'blur');
                }

                await Components.bubbleFocusEvent($target, $relatedTarget, 'focus');
            })
            .catch(() => {});
    }

    /**
     * 컴포넌트 포커스 해제 이벤트를 처리한다.
     * relatedTarget 이 null 이고 페이지가 포커스를 잃은 경우 (페이지 이탈) 에만 onBlur 를 처리한다.
     *
     * @param e - 포커스이벤트
     */
    static focusout(e: FocusEvent): void {
        if (e.relatedTarget !== null) {
            return;
        }

        if (document.hasFocus() == true) {
            return;
        }

        Components.focusQueue = Components.focusQueue
            .then(async () => {
                if (!(e.target instanceof HTMLElement)) {
                    return;
                }

                await Components.bubbleFocusEvent(Html.el(e.target), null, 'blur');
            })
            .catch(() => {});
    }
}

/**
 * 컴포넌트 이벤트 처리를 위해 글로벌 이벤트리스너를 정의한다.
 */
Html.on('keydown', (e: KeyboardEvent) => {
    Components.keydown(e);
});

Html.on('pointerdown', (e: MouseEvent) => {
    Components.pointerdown(e);
});

Html.on('focusin', (e: FocusEvent) => {
    Components.focusin(e);
});

Html.on('focusout', (e: FocusEvent) => {
    Components.focusout(e);
});

Html.on('dragenter', (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files') == true) {
        Components.fileDragEnter();
    }
});

Html.on('dragleave', (e: DragEvent) => {
    if (e.relatedTarget === null && e.dataTransfer?.types.includes('Files') == true) {
        Components.fileDragLeave();
    }
});

Html.on('dragover', (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files') == true) {
        e.preventDefault();
    }
});

Html.on('drop', (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files') == true) {
        e.preventDefault();
        Components.fileDragLeave();
    }
});
